// Frontend mirror of backend/src/helpers/planFeatureCatalog.ts — keep in sync.
// Drives the super-admin plan "Features & Entitlements" editor and the plan-card
// badges. ADDITIVE-ONLY: never rename a key. `enforced` marks flags already
// gated at runtime today (Phase A defines all; Phase B enforces incrementally).

export const PLAN_FEATURE_GROUPS = [
  {
    group: "AI",
    fields: [
      { key: "aiEnabled", label: "AI Auto-Reply & Agents", type: "boolean", enforced: true },
      { key: "aiKnowledgeBaseEnabled", label: "AI Knowledge Base", type: "boolean" },
      { key: "aiRulesEnabled", label: "AI Rules", type: "boolean" },
      { key: "aiSetupAssistantEnabled", label: "AI Setup Assistant", type: "boolean" },
      { key: "gptAssistEnabled", label: "GPT Assist", type: "boolean" },
      { key: "aiBusinessIntelligenceEnabled", label: "AI Business Intelligence", type: "boolean" },
      { key: "aiDashboardEnabled", label: "AI Dashboard", type: "boolean" },
      { key: "contactIntelligenceEnabled", label: "Contact Intelligence", type: "boolean" },
      { key: "videoCatalogEnabled", label: "Video Catalog", type: "boolean" },
    ],
  },
  {
    group: "Channels",
    fields: [
      { key: "whatsappOfficialEnabled", label: "WhatsApp Cloud API (official)", type: "boolean" },
      { key: "whatsappBaileysEnabled", label: "WhatsApp Web / QR", type: "boolean" },
      { key: "facebookMessengerEnabled", label: "Facebook Messenger", type: "boolean" },
      { key: "instagramDmEnabled", label: "Instagram DM", type: "boolean" },
      { key: "telegramEnabled", label: "Telegram", type: "boolean" },
      { key: "emailChannelEnabled", label: "Email channel", type: "boolean", hint: "coming soon" },
    ],
  },
  {
    group: "Messaging & Campaigns",
    fields: [
      { key: "campaignsEnabled", label: "Campaigns", type: "boolean" },
      { key: "whatsappTemplatesEnabled", label: "WhatsApp Templates", type: "boolean" },
      { key: "quickMessagesEnabled", label: "Quick Messages", type: "boolean" },
      { key: "scheduledMessagesEnabled", label: "Scheduled Messages", type: "boolean" },
      { key: "followUpsEnabled", label: "Follow-ups", type: "boolean" },
      { key: "whatsappBackupEnabled", label: "WhatsApp Backup", type: "boolean" },
    ],
  },
  {
    group: "CRM & Tickets",
    fields: [
      { key: "crmIntegration", label: "Contacts CRM", type: "boolean", enforced: true },
      { key: "contactImportEnabled", label: "Contact Import", type: "boolean" },
      { key: "tagsEnabled", label: "Tags", type: "boolean" },
      { key: "ticketSectionsEnabled", label: "Ticket Sections (Kanban)", type: "boolean" },
      { key: "ticketNotesEnabled", label: "Ticket Notes", type: "boolean" },
      { key: "tasksEnabled", label: "Tasks (To-Do)", type: "boolean" },
      { key: "teamsEnabled", label: "Teams", type: "boolean" },
      { key: "internalChatEnabled", label: "Internal Chat", type: "boolean" },
    ],
  },
  {
    group: "Voice",
    fields: [
      { key: "voiceEnabled", label: "Softphone", type: "boolean" },
      { key: "voiceTranscriptionEnabled", label: "Voice Transcription", type: "boolean" },
      { key: "voiceAiAnalysisEnabled", label: "Voice AI Analysis", type: "boolean" },
      { key: "voiceIntelligenceEnabled", label: "Voice Intelligence", type: "boolean" },
      { key: "voiceBrandingEnabled", label: "Voice Branding", type: "boolean" },
    ],
  },
  {
    group: "Automation",
    fields: [
      { key: "actionPipelinesEnabled", label: "Action Pipelines", type: "boolean" },
      { key: "inboundWebhooksEnabled", label: "Inbound Webhooks", type: "boolean" },
      { key: "chatbotEnabled", label: "Chatbot (IVR)", type: "boolean" },
      { key: "appointmentsEnabled", label: "Appointments", type: "boolean" },
      { key: "calendarIntegrationEnabled", label: "Calendar (Google)", type: "boolean" },
      { key: "offersEnabled", label: "Offers", type: "boolean" },
      { key: "pricingRulesEnabled", label: "Pricing Rules", type: "boolean" },
      { key: "externalConnectorsEnabled", label: "External Connectors", type: "boolean" },
    ],
  },
  {
    group: "Payments & Billing",
    fields: [
      { key: "paymentCollectionEnabled", label: "Payment Collection (in-chat)", type: "boolean" },
      { key: "selfServeBillingEnabled", label: "Self-serve Billing", type: "boolean" },
    ],
  },
  {
    group: "Branding & White-label",
    fields: [
      { key: "customBranding", label: "Custom Branding", type: "boolean", enforced: true },
      { key: "customFavicon", label: "Custom Favicon", type: "boolean" },
      { key: "customDomain", label: "Custom Domain", type: "boolean", enforced: true },
      { key: "whiteLabelEnabled", label: "White-label portal", type: "boolean" },
      { key: "ssoEnabled", label: "SSO", type: "boolean" },
    ],
  },
  {
    group: "Analytics & Insights",
    fields: [
      { key: "dashboardEnabled", label: "Dashboard (reports)", type: "boolean" },
      { key: "agentAnalyticsEnabled", label: "Agent Analytics", type: "boolean" },
      { key: "rankingsEnabled", label: "Rankings", type: "boolean" },
    ],
  },
  {
    group: "Support & Platform",
    fields: [
      { key: "publicApiEnabled", label: "Public API", type: "boolean" },
      { key: "pushNotificationsEnabled", label: "Push Notifications", type: "boolean" },
      { key: "inAppNotificationsEnabled", label: "In-app Notifications", type: "boolean" },
      { key: "announcementsEnabled", label: "Announcements", type: "boolean" },
      { key: "filesEnabled", label: "Files (media library)", type: "boolean" },
      { key: "bugReporterEnabled", label: "Bug Reporter", type: "boolean" },
      { key: "prioritySupport", label: "Support tier", type: "enum", options: ["standard", "priority", "dedicated"] },
    ],
  },
  {
    group: "Limits & Caps",
    fields: [
      { key: "maxUsers", label: "Max users", type: "number", enforced: true, hint: "0 = unlimited" },
      { key: "maxConnections", label: "Max connections", type: "number", enforced: true, hint: "0 = unlimited" },
      { key: "maxQueues", label: "Max queues", type: "number", enforced: true, hint: "0 = unlimited" },
      { key: "maxMessagesPerMonth", label: "Max AI msgs / mo", type: "number", enforced: true, hint: "0 = unlimited" },
      { key: "maxCampaignsPerMonth", label: "Max campaigns / mo", type: "number", enforced: true, hint: "0 = unlimited" },
      { key: "maxContacts", label: "Max contacts", type: "number", hint: "0 = unlimited" },
      { key: "maxStorageMb", label: "Max storage (MB)", type: "number", hint: "0 = unlimited" },
    ],
  },
];

// Short labels for the marquee flags worth showing as badges on a plan card.
export const MARQUEE_BADGES = [
  ["aiEnabled", "AI"],
  ["crmIntegration", "CRM"],
  ["voiceEnabled", "Voice"],
  ["campaignsEnabled", "Campaigns"],
  ["customDomain", "Domain"],
  ["customBranding", "Branding"],
  ["whiteLabelEnabled", "White-label"],
  ["publicApiEnabled", "API"],
  ["appointmentsEnabled", "Appointments"],
  ["paymentCollectionEnabled", "Payments"],
];

export const currencySymbol = (c) =>
  ({ ILS: "₪", USD: "$", EUR: "€", GBP: "£" }[(c || "ILS").toUpperCase()] ||
    c ||
    "₪");

export const intervalSuffix = (bi) =>
  bi === "yearly" ? "/yr" : bi === "one_time" ? " one-time" : "/mo";
